import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { AnimatePresence, motion } from "motion/react";
import { MagnifyingGlass, MapPin, X } from "@phosphor-icons/react";
import { useLocation } from "wouter";
import type { Decision, Job, SearchParams } from "../../shared/types.ts";
import { Button, EmptyState, Kbd, MarkLoader, Page, SOURCE_NAMES, Segmented, SourceLogo, cn, spring } from "../ui/index.ts";
import { useMedia } from "../ui/useMedia.ts";
import { ApiError, errorText, getSaved, searchJobs } from "../lib/api.ts";
import { useApp } from "../lib/ctx.ts";
import { ago, FAMILIES, SENIORITY, WORKPLACE } from "../lib/format.ts";
import { Board, Options, PickFilter, TogglePill, type Act } from "./board.tsx";
import { useDecide } from "./parts.tsx";

interface Filters {
  q: string;
  location: string;
  family: string[];
  seniority: string[];
  workplace: string[];
  minSalary: string[];
  postedWithinDays: string[];
  source: string[];
  autoApply: boolean;
}

type Saved = Job & { savedAt: string };
type View = "all" | "saved";

const AUTO_KEY = "jobs.autoApply";
const PAY: Record<string, string> = { "50000": "$50k+", "80000": "$80k+", "100000": "$100k+", "150000": "$150k+", "200000": "$200k+" };
const POSTED: Record<string, string> = { "1": "Past day", "3": "Past 3 days", "7": "Past week", "30": "Past month" };

/** Auto-apply only as the user last left it; off for anyone who never touched it, so the board starts full. */
function savedAutoApply(): boolean {
  try {
    return localStorage.getItem(AUTO_KEY) === "1";
  } catch {
    return false;
  }
}

function saveAutoApply(on: boolean): void {
  try {
    localStorage.setItem(AUTO_KEY, on ? "1" : "0");
  } catch {}
}

const noFilters = (autoApply = savedAutoApply()): Filters => ({
  q: "",
  location: "",
  family: [],
  seniority: [],
  workplace: [],
  minSalary: [],
  postedWithinDays: [],
  source: [],
  autoApply,
});

/** The search params for these filters; auto-apply off means "show everything", not "only on-site". */
function toParams(f: Filters, cursor?: string | null): SearchParams {
  const n = (v: string[]) => (v[0] ? Number(v[0]) : undefined);
  return {
    q: f.q.trim() || undefined,
    location: f.location.trim() || undefined,
    family: f.family.length ? f.family : undefined,
    seniority: f.seniority.length ? f.seniority : undefined,
    workplace: f.workplace.length ? f.workplace : undefined,
    minSalary: n(f.minSalary),
    postedWithinDays: n(f.postedWithinDays),
    source: f.source.length ? f.source : undefined,
    autoApply: f.autoApply || undefined,
    cursor: cursor ?? undefined,
    limit: 25,
  };
}

const without = <T extends { id: string }>(xs: T[], id: string) => xs.filter((x) => x.id !== id);

function putBack<T extends { id: string }>(xs: T[], x: T, at: number): T[] {
  if (xs.some((y) => y.id === x.id)) return xs;
  const next = [...xs];
  next.splice(Math.min(Math.max(at, 0), next.length), 0, x);
  return next;
}

export function Jobs() {
  const [location, navigate] = useLocation();
  const view: View = location.startsWith("/jobs/saved") ? "saved" : "all";
  const app = useApp();
  const decideJob = useDecide();

  const [f, setF] = useState<Filters>(() => noFilters());
  const [items, setItems] = useState<Job[]>([]);
  const [fresh, setFresh] = useState<Set<string>>(() => new Set());
  const [cursor, setCursor] = useState<string | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "error">("loading");
  const [more, setMore] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [saved, setSaved] = useState<Saved[] | null>(null);
  const [savedFresh, setSavedFresh] = useState<Set<string>>(() => new Set());
  const [savedFailed, setSavedFailed] = useState(false);
  const [marks, setMarks] = useState<Record<string, Decision | null>>({});
  const sentinel = useRef<HTMLDivElement>(null);
  const req = useRef(0);

  useEffect(() => {
    const id = ++req.current;
    setState("loading");
    searchJobs(toParams(f)).then(
      (r) => {
        if (id !== req.current) return;
        setItems(r.items);
        setFresh(new Set(r.items.map((j) => j.id)));
        setCursor(r.nextCursor);
        setState("ready");
      },
      () => id === req.current && setState("error"),
    );
  }, [f, attempt]);

  const loadSaved = useCallback(() => {
    setSavedFailed(false);
    getSaved().then(
      (r) => {
        setSaved([...r].sort((a, b) => b.savedAt.localeCompare(a.savedAt)));
        setSavedFresh(new Set(r.map((j) => j.id)));
      },
      () => setSavedFailed(true),
    );
  }, []);
  useEffect(loadSaved, [loadSaved]);

  const next = useCallback(() => {
    if (!cursor || more || view !== "all") return;
    const id = req.current;
    setMore(true);
    searchJobs(toParams(f, cursor))
      .then((r) => {
        if (id !== req.current) return;
        setItems((xs) => {
          const have = new Set(xs.map((x) => x.id));
          return [...xs, ...r.items.filter((x) => !have.has(x.id))];
        });
        setCursor(r.nextCursor);
      })
      .catch((e) => {
        if (id !== req.current) return;
        if (e instanceof ApiError && e.error === "bad_cursor") setCursor(null);
        app.toast(errorText(e, "More jobs didn't load."), "bad");
      })
      .finally(() => setMore(false));
  }, [cursor, more, f, view, app]);

  useEffect(() => {
    const el = sentinel.current;
    if (!el) return;
    const root = el.closest<HTMLElement>("[data-board-scroll]");
    const io = new IntersectionObserver((e) => e[0]?.isIntersecting && next(), { root, rootMargin: "800px" });
    io.observe(el);
    return () => io.disconnect();
  }, [next, items.length]);

  const savedIds = useMemo(() => new Set(saved?.map((s) => s.id)), [saved]);
  const listed = useMemo(() => new Map(items.map((j) => [j.id, j.decision ?? null])), [items]);
  const markOf = (id: string): Decision | null => (id in marks ? marks[id]! : savedIds.has(id) ? "save" : (listed.get(id) ?? null));
  const setMark = (id: string, d: Decision | null) => setMarks((m) => ({ ...m, [id]: d }));

  const act: Act = (d, job, from) => {
    const before = markOf(job.id);
    if (d === "save") {
      const on = before !== "save";
      setMark(job.id, on ? "save" : null);
      const at = saved?.findIndex((s) => s.id === job.id) ?? -1;
      setSaved((xs) => xs && (on ? [{ ...job, savedAt: new Date().toISOString() }, ...without(xs, job.id)] : without(xs, job.id)));
      decideJob(job, "save", before, from).then((m) => {
        setMark(job.id, m);
        if ((m === "save") === on) return;
        const old = { ...job, savedAt: new Date().toISOString() };
        setSaved((xs) => xs && (m === "save" ? putBack(xs, old, at) : without(xs, job.id)));
      });
      return;
    }
    const at = items.findIndex((j) => j.id === job.id);
    const savedAt = saved?.findIndex((s) => s.id === job.id) ?? -1;
    const savedRow = saved?.[savedAt];
    setMark(job.id, d);
    setItems((xs) => without(xs, job.id));
    setSaved((xs) => xs && without(xs, job.id));
    decideJob(job, d, before, from).then((m) => {
      setMark(job.id, m);
      if (m === d) return;
      if (at >= 0) setItems((xs) => putBack(xs, job, at));
      if (savedRow && m === "save") setSaved((xs) => xs && putBack(xs, savedRow, savedAt));
    });
  };

  const send = (job: Job, from: Element) => decideJob(job, "apply", markOf(job.id), from).then((m) => m === "apply");

  const sent = (job: Job) => {
    setMark(job.id, "apply");
    setItems((xs) => without(xs, job.id));
    setSaved((xs) => xs && without(xs, job.id));
  };

  const applied = (job: Job, on: boolean) => setMark(job.id, on ? "apply" : null);
  const sends = { onSend: send, onSent: sent, onApplied: applied };

  const dirty = JSON.stringify(f) !== JSON.stringify(noFilters(f.autoApply));
  const set = <K extends keyof Filters>(k: K, v: Filters[K]) => setF((x) => ({ ...x, [k]: v }));
  const clear = () => setF((x) => noFilters(x.autoApply));
  const toggleAutoApply = () => {
    saveAutoApply(!f.autoApply);
    set("autoApply", !f.autoApply);
  };
  const count = saved?.length;
  const toggle = (
    <Segmented
      aria-label="Jobs"
      value={view}
      onChange={(v) => navigate(v === "saved" ? "/jobs/saved" : "/jobs")}
      options={[
        { value: "all", label: "All jobs" },
        { value: "saved", label: "Saved", count },
      ]}
    />
  );

  return (
    <Page
      title={
        <AnimatePresence initial={false} mode="popLayout">
          <motion.span
            key={view}
            initial={{ opacity: 0, y: 10, filter: "blur(4px)" }}
            animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
            exit={{ opacity: 0, y: -10, filter: "blur(4px)" }}
            transition={spring}
            className="inline-block"
          >
            {view === "all" ? "All jobs." : "Saved jobs."}
          </motion.span>
        </AnimatePresence>
      }
      action={<div className="hidden sm:block">{toggle}</div>}
    >
      <div className="-mt-2 sm:hidden">{toggle}</div>
      {view === "all" ? (
        <>
          <SearchBar
            q={f.q}
            location={f.location}
            onSearch={(q, location) => setF((x) => ({ ...x, q, location }))}
            railed={state !== "error" && items.length > 0}
            filters={
              <>
                <TogglePill on={f.autoApply} onClick={toggleAutoApply}>
                  Auto-apply
                </TogglePill>
                <PickFilter label="Field" multi value={f.family} options={FAMILIES} onChange={(v) => set("family", v)} />
                <PickFilter label="Level" multi value={f.seniority} options={SENIORITY} onChange={(v) => set("seniority", v)} />
                <PickFilter label="Workplace" multi value={f.workplace} options={WORKPLACE} onChange={(v) => set("workplace", v)} />
                <PickFilter label="Pay" value={f.minSalary} options={PAY} onChange={(v) => set("minSalary", v)} />
                <PickFilter label="Posted" value={f.postedWithinDays} options={POSTED} onChange={(v) => set("postedWithinDays", v)} />
                <PickFilter label="Source" multi value={f.source} options={SOURCE_NAMES} onChange={(v) => set("source", v)} />
                <AnimatePresence initial={false}>
                  {dirty && (
                    <motion.button
                      type="button"
                      initial={{ opacity: 0, x: -6 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -6 }}
                      transition={spring}
                      onClick={clear}
                      className="h-9 shrink-0 rounded-control px-3 text-sm font-semibold text-primary-ink transition-[background-color] duration-150 hover:bg-primary-soft"
                    >
                      Clear all
                    </motion.button>
                  )}
                </AnimatePresence>
              </>
            }
          />
          {state === "error" ? (
            <EmptyState
              title="Search didn't load."
              action={
                <Button variant="secondary" onClick={() => setAttempt((n) => n + 1)}>
                  Try again
                </Button>
              }
            />
          ) : state === "loading" && !items.length ? (
            <MarkLoader label="Searching open jobs" />
          ) : state === "ready" && !items.length ? (
            <EmptyState
              art="search"
              title="Nothing matches."
              action={
                dirty || f.autoApply ? (
                  <Button variant="secondary" onClick={() => setF(noFilters(false))}>
                    Clear your filters
                  </Button>
                ) : undefined
              }
            />
          ) : (
            <Board
              items={items}
              fresh={fresh}
              mark={markOf}
              onAct={act}
              {...sends}
              dim={state === "loading"}
              header={<ResultsHeader n={items.length} more={!!cursor} apply={items.some((j) => j.canAutoApply)} />}
              rail={<Rail f={f} set={set} dirty={dirty} clear={clear} onAutoApply={toggleAutoApply} />}
              footer={
                <div ref={sentinel} className="flex min-h-10 justify-center">
                  {more && <MarkLoader layout="row" label="Loading more jobs" />}
                </div>
              }
            />
          )}
        </>
      ) : savedFailed && !saved ? (
        <EmptyState
          title="Your saved jobs didn't load."
          action={
            <Button variant="secondary" onClick={loadSaved}>
              Try again
            </Button>
          }
        />
      ) : !saved ? (
        <MarkLoader label="Loading your saved jobs" />
      ) : !saved.length ? (
        <EmptyState
          art="saved"
          title="Nothing saved yet."
          text="Tap the bookmark on any job to keep it here."
          action={<Button onClick={() => navigate("/jobs")}>Browse all jobs</Button>}
        />
      ) : (
        <Board
          items={saved}
          fresh={savedFresh}
          dropOnUnsave
          mark={markOf}
          onAct={act}
          {...sends}
          stamp={(j) => `Saved ${ago((j as Saved).savedAt)}`}
          header={<ResultsHeader n={saved.length} saved apply={saved.some((j) => j.canAutoApply)} />}
        />
      )}
    </Page>
  );
}

function ResultsHeader({ n, more = false, saved = false, apply = false }: { n: number; more?: boolean; saved?: boolean; apply?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-3 px-1">
      <p className="text-[13px] font-semibold text-ink-soft tabular-nums">
        {saved ? `${n} saved ${n === 1 ? "job" : "jobs"}` : `Showing ${n}${more ? "+" : ""} ${n === 1 && !more ? "job" : "jobs"}`}
      </p>
      <p className="hidden items-center gap-1.5 text-[12.5px] text-muted lg:flex">
        <Kbd keys={["j", "k"]} />
        to move
        {apply && (
          <>
            <Kbd keys="a" className="ml-2" />
            to apply
          </>
        )}
      </p>
    </div>
  );
}

const FIELDS_SHOWN = 6;

/** Every filter as a column of checkbox groups, beside the list on wide screens. */
function Rail({
  f,
  set,
  dirty,
  clear,
  onAutoApply,
}: {
  f: Filters;
  set: <K extends keyof Filters>(k: K, v: Filters[K]) => void;
  dirty: boolean;
  clear: () => void;
  onAutoApply: () => void;
}) {
  const [allFields, setAllFields] = useState(false);
  const fields = allFields ? FAMILIES : Object.fromEntries(Object.entries(FAMILIES).filter(([k], i) => i < FIELDS_SHOWN || f.family.includes(k)));
  return (
    <div className="flex flex-col gap-5">
      <button
        type="button"
        role="switch"
        aria-checked={f.autoApply}
        onClick={onAutoApply}
        className={cn(
          "flex items-start gap-3 rounded-card p-3 text-left transition-[background-color,box-shadow] duration-150",
          f.autoApply ? "bg-accent-soft ring-1 ring-accent/40" : "bg-surface shadow-card hover:bg-sunk",
        )}
      >
        <span className="min-w-0 flex-1">
          <span className="block text-[13.5px] font-bold text-ink">Auto-apply only</span>
          <span className="mt-0.5 block text-[12.5px] leading-snug text-muted">Jobs Headcount can send for you.</span>
        </span>
        <span aria-hidden className={cn("relative mt-0.5 h-5 w-9 shrink-0 rounded-chip transition-[background-color] duration-150", f.autoApply ? "bg-accent" : "bg-ink/15")}>
          <motion.span
            className="absolute top-0.5 size-4 rounded-sm bg-white shadow-soft"
            initial={false}
            animate={{ left: f.autoApply ? 18 : 2 }}
            transition={spring}
          />
        </span>
      </button>
      <RailGroup title="Field" count={f.family.length} onClear={() => set("family", [])}>
        <Options compact multi value={f.family} options={fields} onChange={(v) => set("family", v)} />
        <button
          type="button"
          onClick={() => setAllFields((x) => !x)}
          className="mt-0.5 self-start rounded-control px-2 py-1 text-[13px] font-semibold text-primary-ink hover:bg-primary-soft"
        >
          {allFields ? "Show fewer" : `Show all ${Object.keys(FAMILIES).length}`}
        </button>
      </RailGroup>
      <RailGroup title="Level" count={f.seniority.length} onClear={() => set("seniority", [])}>
        <Options compact multi value={f.seniority} options={SENIORITY} onChange={(v) => set("seniority", v)} />
      </RailGroup>
      <RailGroup title="Workplace" count={f.workplace.length} onClear={() => set("workplace", [])}>
        <Options compact multi value={f.workplace} options={WORKPLACE} onChange={(v) => set("workplace", v)} />
      </RailGroup>
      <RailGroup title="Pay" count={f.minSalary.length} onClear={() => set("minSalary", [])}>
        <Options compact value={f.minSalary} options={PAY} onChange={(v) => set("minSalary", v)} />
      </RailGroup>
      <RailGroup title="Posted" count={f.postedWithinDays.length} onClear={() => set("postedWithinDays", [])}>
        <Options compact value={f.postedWithinDays} options={POSTED} onChange={(v) => set("postedWithinDays", v)} />
      </RailGroup>
      <RailGroup title="Source" count={f.source.length} onClear={() => set("source", [])}>
        <Options compact multi value={f.source} options={SOURCE_NAMES} onChange={(v) => set("source", v)} render={(k, v) => (
            <span className="flex items-center gap-2">
              <span aria-hidden className="inline-flex">
                <SourceLogo source={k} size="xs" />
              </span>
              {v}
            </span>
          )} />
      </RailGroup>
      {dirty && (
        <Button variant="secondary" size="sm" onClick={clear} className="self-start">
          Clear all filters
        </Button>
      )}
    </div>
  );
}

function RailGroup({ title, count, onClear, children }: { title: string; count: number; onClear: () => void; children: React.ReactNode }) {
  return (
    <section className="flex flex-col">
      <div className="mb-1 flex h-6 items-center justify-between px-2">
        <h3 className="text-[12.5px] font-bold text-ink">{title}</h3>
        {count > 0 && (
          <button type="button" onClick={onClear} className="text-[12px] font-semibold text-primary-ink hover:underline">
            Clear
          </button>
        )}
      </div>
      {children}
    </section>
  );
}

function SearchBar({
  q,
  location,
  onSearch,
  filters,
  railed,
}: {
  q: string;
  location: string;
  onSearch: (q: string, location: string) => void;
  filters: React.ReactNode;
  /** The board shows its filter rail, so the pill row hides on wide screens. */
  railed: boolean;
}) {
  const [text, setText] = useState(q);
  const [where, setWhere] = useState(location);
  const [focus, setFocus] = useState(false);
  const sm = useMedia("(min-width: 640px)");
  useEffect(() => setText(q), [q]);
  useEffect(() => setWhere(location), [location]);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    (document.activeElement as HTMLElement | null)?.blur();
    onSearch(text.trim(), where.trim());
  };

  return (
    <div className="sticky top-14 z-20 -mx-4 -mt-2 flex flex-col gap-3 bg-page/90 px-4 pb-3 pt-2 backdrop-blur-xl md:top-0 md:mx-0 md:px-0 lg:static lg:bg-transparent lg:p-0 lg:backdrop-blur-none">
      <form
        role="search"
        onSubmit={submit}
        onFocus={() => setFocus(true)}
        onBlur={(e) => !e.currentTarget.contains(e.relatedTarget as Node | null) && setFocus(false)}
        className="flex flex-col rounded-card bg-surface p-1 shadow-card transition-[box-shadow] duration-150 focus-within:shadow-[0_0_0_2px_var(--primary)] sm:flex-row sm:items-center sm:gap-1"
      >
        <div className="flex min-w-0 items-center gap-1 sm:flex-[1.6]">
          <SearchField
            icon={<MagnifyingGlass weight="bold" />}
            value={text}
            onChange={setText}
            onClear={() => {
              setText("");
              onSearch("", where.trim());
            }}
            placeholder="Title, company or skill"
            label="Title, company or skill"
            className="min-w-0 flex-1"
          />
          <Button type="submit" className="size-10 px-0 sm:hidden" aria-label="Search">
            <MagnifyingGlass size={18} weight="bold" aria-hidden />
          </Button>
        </div>
        <span className="hidden h-7 w-px shrink-0 bg-line-strong sm:block" aria-hidden />
        <AnimatePresence initial={false}>
          {(sm || focus || where) && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ height: spring, opacity: { duration: 0.15 } }}
              className="min-w-0 overflow-hidden sm:flex-1"
            >
              <div className="mx-2 mt-1 border-t border-line pt-1 sm:m-0 sm:border-0 sm:p-0">
                <SearchField
                  icon={<MapPin weight="bold" />}
                  value={where}
                  onChange={setWhere}
                  onClear={() => {
                    setWhere("");
                    onSearch(text.trim(), "");
                  }}
                  placeholder="City, state or remote"
                  label="Location"
                  className="-mx-2 sm:mx-0"
                />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
        <Button type="submit" className="hidden h-10 px-6 sm:inline-flex">
          Search
        </Button>
      </form>
      <div className={cn("-mx-4 flex items-center gap-2 overflow-x-auto px-4 py-0.5 [scrollbar-width:none] md:mx-0 md:flex-wrap md:overflow-visible md:px-0", railed && "xl:hidden")}>
        {filters}
      </div>
    </div>
  );
}

/** One field of a search box: an icon, the input, and a clear button once it has text. */
export function SearchField({
  icon,
  value,
  onChange,
  onClear,
  placeholder,
  label,
  className,
}: {
  icon: React.ReactNode;
  value: string;
  onChange: (v: string) => void;
  onClear: () => void;
  placeholder: string;
  label: string;
  className?: string;
}) {
  return (
    <label className={cn("group relative flex h-10 items-center", className)}>
      <span
        className="pointer-events-none absolute left-3.5 text-muted transition-[color] duration-150 group-focus-within:text-primary [&_svg]:size-[18px]"
        aria-hidden
      >
        {icon}
      </span>
      <input
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-label={label}
        enterKeyHint="search"
        className="h-full w-full min-w-0 truncate rounded-control bg-transparent pl-10 pr-9 text-[15px] font-medium text-ink placeholder:font-normal placeholder:text-muted focus:outline-none [&::-webkit-search-cancel-button]:appearance-none"
      />
      <AnimatePresence initial={false}>
        {value && (
          <motion.button
            type="button"
            aria-label={`Clear ${label.toLowerCase()}`}
            onClick={onClear}
            initial={{ opacity: 0, scale: 0.6 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.6 }}
            transition={spring}
            className="absolute right-1 grid size-8 place-items-center rounded-control text-muted transition-[background-color,color] duration-150 hover:bg-ink/[0.06] hover:text-ink"
          >
            <X size={16} weight="bold" aria-hidden />
          </motion.button>
        )}
      </AnimatePresence>
    </label>
  );
}
